import { useSheetValues } from '@/hooks/sheet/useSheetValues'
import {
  Button,
  cn,
  FieldFilterDropdown,
  FilterCollection,
  FilterGroup,
  FilterOption,
  FilterQueryCollection,
} from '@flatfile/baseline'

import { useQuery } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useFilterSheet } from '../sheet/store/hooks/useFilterSheet'
import { SheetQuery } from '../sheet/store/useSheetStore'

const SheetFilterArea = ({
  sheetId,
  onFieldSelected,
  fieldOptionGroups,
}: {
  sheetId: string
  onFieldSelected: (field: string) => void
  fieldOptionGroups: FilterGroup[]
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')
  const [valueOptions, setValueOptions] = useState<FilterOption[]>([])

  const {
    activeQueryId,
    hasFilters,
    setActiveQuery,
    queryCollections,
    activeQueryCollectionId,
    createCollection,
    removeCollection,
    clearCollections,
    addCollectionQuery,
    updateCollectionQuery,
    removeCollectionQuery,
    setActiveQueryCollection,
    updateCollection,
  } = useFilterSheet(sheetId)
  const { getCellValues } = useSheetValues(sheetId)

  const collectionBlocks = Object.values(queryCollections || {})
  const activeCollection = activeQueryCollectionId
    ? queryCollections[activeQueryCollectionId]
    : null
  const activeQuery = activeCollection?.queries.find(
    (q: SheetQuery) => q.id === activeQueryId
  )
  const PAGE_SIZE = 1000

  const { data: cellValues, isLoading: isLoadingCellValues } = useQuery({
    queryKey: [
      'cellValues',
      sheetId,
      activeQuery?.queryField,
      debouncedSearchValue,
    ],
    queryFn: async () => {
      return await getCellValues({
        fieldKey: activeQuery?.queryField,
        searchValue: debouncedSearchValue,
        pageSize: PAGE_SIZE,
      })
    },
    enabled: !!activeQuery?.queryField,
    staleTime: 0,
  })

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchValue])

  useEffect(() => {
    if (cellValues?.data?.values) {
      const options = cellValues.data.values.map((value) => ({
        label: value,
        key: value,
      }))
      setValueOptions(options)
    }
  }, [cellValues?.data?.values])

  const handleRemoveQuery = (collectionId: string, queryId: string) => {
    removeCollectionQuery(collectionId, queryId)
  }

  const handleRemoveCollection = (collectionId: string) => {
    removeCollection(collectionId)
  }

  const handleActivateQuery = (queryId: string | undefined) => {
    setActiveQuery(queryId)
  }

  const handleActivateCollection = (collectionId: string | undefined) => {
    setActiveQueryCollection(collectionId)
  }

  const handleCreateAdvancedFilter = () => {
    const createdCollection = createCollection(undefined, 'advanced')
    setActiveQueryCollection(createdCollection.id)
  }

  const handleUpdateCollectionQuery = (
    collectionId: string,
    query: SheetQuery
  ) => {
    updateCollectionQuery(collectionId, query)
  }

  const handleUpdateCollection = (
    collectionId: string,
    updatedCollection: Partial<FilterQueryCollection>
  ) => {
    updateCollection(collectionId, updatedCollection)
  }

  const handleAddCollectionQuery = (
    collectionId: string,
    query: Partial<Omit<SheetQuery, 'id'>>
  ) => {
    const createdQuery = addCollectionQuery(
      collectionId,
      query as Omit<SheetQuery, 'id'>
    )
    setActiveQuery(createdQuery.id)
  }

  const advancedFilterAction = {
    label: 'Advanced filter',
    icon: 'settings-slider-three',
    onClick: handleCreateAdvancedFilter,
  }

  return (
    <AnimatePresence>
      {hasFilters && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className={cn('w-full overflow-hidden')}
        >
          <div className='flex items-center gap-2'>
            {collectionBlocks.map((collection: FilterQueryCollection) => (
              <FilterCollection
                key={collection.id}
                queryCollection={collection}
                searchValue={searchValue}
                activeCollectionId={activeQueryCollectionId}
                activeQueryId={activeQueryId}
                isPending={isLoadingCellValues}
                filterOperations={filterOperationGroups}
                valueOptionsList={valueOptions}
                fieldOptionGroups={fieldOptionGroups}
                onAddCollectionQuery={handleAddCollectionQuery}
                onUpdateCollectionQuery={handleUpdateCollectionQuery}
                onCollectionActivate={handleActivateCollection}
                onQueryActivate={handleActivateQuery}
                onRemoveQuery={handleRemoveQuery}
                onRemoveCollection={handleRemoveCollection}
                onSearchValueChange={setSearchValue}
                onUpdateCollection={handleUpdateCollection}
              />
            ))}
            {collectionBlocks.length > 0 && (
              <FieldFilterDropdown
                onFieldSelected={onFieldSelected}
                options={fieldOptionGroups}
                footerActions={[advancedFilterAction]}
              >
                <Button variant='ghost' size='icon-md' icon='plus-small' />
              </FieldFilterDropdown>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// Supported FFQL Operations for Filtering
const comparisonOperations = [
  { label: 'is', key: 'eq' },
  { label: 'is not', key: 'ne' },
] as any as FilterOption[]

const validationOperations = [
  { label: 'is empty', key: 'is empty' },
  { label: 'not empty', key: 'not empty' },
] as any as FilterOption[]

const sizeOperations = [
  { label: 'greater than', key: 'gt' },
  { label: 'less than', key: 'lt' },
] as any as FilterOption[]

const filterOperationGroups = [
  { options: comparisonOperations },
  { options: validationOperations },
  { options: sizeOperations },
] as FilterGroup[]

export default SheetFilterArea
